import React, { useEffect, useMemo, useState } from "react";
import { parseDate } from "@/lib/dates";
import { useNavigate } from "react-router-dom";
import { Plus, Calendar, Users as UsersIcon, FileDown, Trash2, Pencil, Search, X, Wand2, Copy, ChevronDown, ChevronRight, Archive, RotateCcw, AlertTriangle } from "lucide-react";
import api, { API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { TravefyImportModal } from "./TravefyImportModal";
import { confirmAsync } from "@/lib/safeConfirm";
import { usePartners } from "@/lib/partners";

const STATUS_LABEL = {
  draft: { text: "Borrador", color: "bg-clay-200 text-clay-900" },
  sold: { text: "Vendido", color: "bg-pine text-white" },
  not_sold: { text: "No vendido", color: "bg-clay-400 text-white" },
  extras: { text: "Extras", color: "bg-terracotta text-white" },
};

function fmt(d) {
  if (!d) return "—";
  try { return parseDate(d).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return d; }
}

function calcTotals(itn) {
  let excl = 0, incl = 0;
  (itn.days || []).forEach((day) => {
    (day.services || []).forEach((s) => {
      excl += (s.unit_price_tax_excl || 0) * (s.quantity || 0);
      incl += (s.unit_price_tax_incl || s.unit_price || 0) * (s.quantity || 0);
    });
  });
  (itn.accommodations || []).forEach((a) => {
    excl += a.price_tax_excl || 0;
    incl += a.price_tax_incl || a.price || 0;
  });
  const pvp = incl * (1 + (itn.markup_pct || 0) / 100);
  return { subtotal: incl, final: pvp, excl };
}

const agentName = (email) => {
  if (!email) return "—";
  const local = email.split("@")[0];
  return local.charAt(0).toUpperCase() + local.slice(1);
};

export default function Dashboard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [archivedCount, setArchivedCount] = useState(0);
  const [viewMode, setViewMode] = useState("active"); // "active" | "archived"
  const [stats, setStats] = useState({ providers: 0, experiences: 0, itineraries: 0 });
  const [agents, setAgents] = useState([]);
  // Filter defaults to the current user's own email — every agent lands on
  // their own trips first and only widens the view when they change the
  // filter. This replaces the old owner+shared_with permission split.
  const [filterAgent, setFilterAgent] = useState(user?.email || "");
  const [filterTraveler, setFilterTraveler] = useState("");
  const [filterStatus, setFilterStatus] = useState("");  // "" = todos
  const [loading, setLoading] = useState(true);
  const [showTravefy, setShowTravefy] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());  // group ids that show older versions
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterAgent) params.agent = filterAgent;
      if (filterTraveler) params.traveler = filterTraveler;
      if (viewMode === "archived") params.archived = true;
      // Kick off the main listing + stats + the agent-filter list in
      // parallel with a lightweight count fetch of the OTHER view so we
      // can badge the "Archived" tab without a second full-page load when
      // switching modes.
      const otherParams = { ...params, archived: viewMode !== "archived" ? true : false };
      const res = await Promise.all([
        api.get("/itineraries", { params }),
        api.get("/stats"),
        api.get("/itineraries", { params: otherParams }),
        api.get("/itineraries/agents"),
      ]);
      setItems(res[0].data); setStats(res[1].data);
      if (viewMode === "archived") {
        setArchivedCount(res[0].data.length);
      } else {
        setArchivedCount(res[2].data.length);
      }
      setAgents(res[3].data.agents || []);
    } finally { setLoading(false); }
  };

  // Reload on filter or view-mode changes. `load` is intentionally NOT in
  // the deps (it captures the latest state via closure each render; adding
  // it would cause an infinite loop). The debounce timer coalesces rapid
  // changes and load() always uses the latest state at call time.
  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [filterAgent, filterTraveler, viewMode]);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTripPartner, setNewTripPartner] = useState("");
  const { visible: partnerVisible, defaults: partnerDefaultsOf, defaultKey: partnerDefaultKey } = usePartners();

  const openCreate = () => {
    setNewTripPartner(partnerDefaultKey);
    setShowCreateModal(true);
  };

  const create = async () => {
    setShowCreateModal(false);
    const today = new Date();
    // Ship the partner defaults from the selected source so a Zicasso trip is
    // born with markup 30 + commission 11.73, KimKim with 33 + 15, etc.
    // Previously this endpoint sent a hardcoded `markup_pct: 15` which
    // ignored every partner default and forced agents to click ↻ AUTO on
    // every new trip — the root cause of the "commission stays at 15 %"
    // complaint. We now push the correct defaults ONCE at creation time and
    // never touch existing itineraries.
    const partner = newTripPartner || partnerDefaultKey;
    const defaults = partnerDefaultsOf(partner) || { markup_pct: 30, commission_pct: 0 };
    const { data } = await api.post("/itineraries", {
      name: "Nuevo itinerario",
      start_date: today.toISOString().slice(0,10),
      end_date: new Date(today.getTime() + 6*86400000).toISOString().slice(0,10),
      num_travelers: 2,
      partner,
      markup_pct: defaults.markup_pct,
      commission_pct: defaults.commission_pct,
      days: [],
    });
    navigate(`/itineraries/${data.itinerary_id}`);
  };

  const archive = async (id) => {
    if (!(await confirmAsync(
      "¿Archivar este itinerario?\n\nSe ocultará del listado activo, pero se conserva íntegro (pagos, T&C, todo) y puedes restaurarlo cuando quieras desde la pestaña «Archivados».",
      { destructive: true, confirmLabel: "Archivar" }
    ))) return;
    try {
      await api.delete(`/itineraries/${id}`);
      toast.success("Archivado — restáuralo desde la pestaña «Archivados»");
      // Optimistic UI: drop the row locally, then refresh totals + archived count.
      setItems((prev) => prev.filter((it) => it.itinerary_id !== id));
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Error al archivar";
      toast.error(detail);
      console.error("archive itinerary failed:", e);
    }
  };

  const restore = async (id) => {
    try {
      await api.post(`/itineraries/${id}/restore`);
      toast.success("Restaurado — vuelve a aparecer en la lista activa");
      setItems((prev) => prev.filter((it) => it.itinerary_id !== id));
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Error al restaurar";
      toast.error(detail);
    }
  };

  const purge = async (id, name) => {
    if (!(await confirmAsync(
      `¿Borrar «${name}» PERMANENTEMENTE?\n\nEsta acción no se puede deshacer. El itinerario se elimina de la base de datos principal (se conserva una copia de seguridad en la colección «itineraries_archived» por si un admin necesita restaurarla manualmente).\n\nEscribe la palabra en tu cabeza: definitivo.`,
      { destructive: true, confirmLabel: "Borrar permanentemente" }
    ))) return;
    try {
      await api.delete(`/itineraries/${id}/permanent`);
      toast.success("Itinerario borrado definitivamente");
      setItems((prev) => prev.filter((it) => it.itinerary_id !== id));
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "No se pudo borrar";
      toast.error(detail);
    }
  };

  const duplicate = async (id) => {
    try {
      const { data } = await api.post(`/itineraries/${id}/duplicate`);
      toast.success(`Versión v${data.version} creada`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo duplicar");
    }
  };

  // Group itineraries by version_group_id and pick the latest version as the
  // "main" row. Older versions stay nested and are revealed when the agent
  // clicks the chevron. Legacy items without a group_id default to their own
  // singleton group (the backend backfills this on startup, but we keep this
  // fallback so the UI is robust on partial migrations).
  const groups = useMemo(() => {
    const map = new Map();
    for (const it of items) {
      if (filterStatus && (it.status || "draft") !== filterStatus) continue;
      const gid = it.version_group_id || it.itinerary_id;
      if (!map.has(gid)) map.set(gid, []);
      map.get(gid).push(it);
    }
    // Sort versions inside each group: newest version first
    const groupList = [];
    for (const [gid, versions] of map.entries()) {
      versions.sort((a, b) => (b.version || 1) - (a.version || 1));
      groupList.push({ gid, versions });
    }
    // Sort groups by the latest version's updated_at, newest first (matches
    // the backend's existing sort so the table feels stable)
    groupList.sort((a, b) =>
      new Date(b.versions[0].updated_at || 0) - new Date(a.versions[0].updated_at || 0)
    );
    return groupList;
  }, [items, filterStatus]);

  const toggleGroup = (gid) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(gid)) next.delete(gid); else next.add(gid);
      return next;
    });
  };

  const exportXlsx = async (id, name) => {
    const url = `${API_BASE}/itineraries/${id}/export`;
    try {
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${name || "itinerario"}.xlsx`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (e) { toast.error("No se pudo exportar"); }
  };

  // Reset filters back to the default view (only my own trips). We NEVER
  // reset to blank — an empty agent filter means "everyone", which is a
  // deliberate action the user takes via the dropdown, not a reset.
  const clearFilters = () => { setFilterAgent(user?.email || ""); setFilterTraveler(""); setFilterStatus(""); };
  // "hasFilters" flags any deviation from the default view so the "Limpiar"
  // chip only shows when the user has actively widened or narrowed things.
  const hasFilters = filterAgent !== (user?.email || "") || Boolean(filterTraveler) || Boolean(filterStatus);
  const isMyView = filterAgent === user?.email;

  // Column grid: agent name is always shown (all users see who created each
  // itinerary). Previously admin-only, but now that managers see the whole
  // team's trips and shared-with propagates itineraries across agents, every
  // user needs the ownership column to avoid confusion.
  const gridCols = "grid-cols-[1.4fr_0.9fr_1fr_1fr_0.6fr_0.9fr_auto]";

  return (
    <div className="px-8 py-8 max-w-7xl">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="smallcaps">Centro de operaciones</div>
          <h1 className="font-serif text-5xl leading-none mt-3">Itinerarios</h1>
          <p className="text-sm text-clay-700 mt-3 max-w-lg">
            Todos los itinerarios de la agencia están disponibles. Por defecto ves los tuyos; cambia el filtro de agente para revisar los de un compañero.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="new-from-travefy-btn"
            onClick={() => setShowTravefy(true)}
            className="inline-flex items-center gap-2 px-5 py-3 border border-clay-900 text-clay-900 text-sm tracking-wider uppercase hover:bg-clay-100 transition-colors"
            title="Importa un itinerario publicado de Travefy"
          >
            <Wand2 size={16} /> Nuevo desde Travefy
          </button>
          <button data-testid="new-itinerary-btn" onClick={openCreate} className="inline-flex items-center gap-2 px-5 py-3 bg-clay-900 text-white text-sm tracking-wider uppercase hover:bg-terracotta transition-colors">
            <Plus size={16} /> Nuevo itinerario
          </button>
        </div>
      </div>
      {showTravefy && <TravefyImportModal onClose={() => { setShowTravefy(false); load(); }} />}
      {showCreateModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
          onClick={() => setShowCreateModal(false)}
          data-testid="create-itinerary-modal"
        >
          <div
            className="w-full max-w-md bg-espiritu-sand border border-espiritu-sand-deep p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="font-serif text-2xl text-clay-900">Nuevo itinerario</div>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-clay-700 hover:text-clay-900"
                data-testid="create-modal-close"
              ><X size={18}/></button>
            </div>
            <div className="text-xs text-clay-700 mb-4 leading-relaxed">
              Elige el <strong>partner de origen</strong>. El markup y la comisión se rellenan
              automáticamente con los valores negociados de ese partner (podrás editarlos después
              si lo necesitas).
            </div>
            <label className="block text-[10px] tracking-[0.25em] uppercase text-clay-700 mb-2">
              Partner
            </label>
            <select
              value={newTripPartner}
              onChange={(e) => setNewTripPartner(e.target.value)}
              data-testid="create-partner-select"
              className="w-full bg-white border border-espiritu-sand-deep px-3 py-2 text-sm text-clay-900 focus:outline-none focus:border-terracotta mb-2"
            >
              {partnerVisible.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label} — {p.hint}
                </option>
              ))}
            </select>
            <div className="text-[11px] text-clay-700 mb-6 bg-white border border-espiritu-sand-deep px-3 py-2">
              Se creará con&nbsp;
              <strong>markup {partnerDefaultsOf(newTripPartner)?.markup_pct}%</strong>
              &nbsp;·&nbsp;
              <strong>comisión {partnerDefaultsOf(newTripPartner)?.commission_pct}%</strong>
            </div>
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-sm text-clay-700 hover:text-clay-900"
                data-testid="create-modal-cancel"
              >Cancelar</button>
              <button
                onClick={create}
                className="px-5 py-2 bg-clay-900 hover:bg-terracotta text-white text-sm tracking-wider uppercase transition-colors"
                data-testid="create-modal-confirm"
              >Crear</button>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-3 gap-0 mb-8 border border-clay-300">
        {[
          {
            l: isMyView ? "Mis itinerarios" : filterAgent ? "Itinerarios del agente" : "Itinerarios totales",
            v: filterAgent ? items.length : stats.itineraries,
            t: "stat-itineraries",
          },
          { l: "Experiencias", v: stats.experiences, t: "stat-experiences" },
          { l: "Proveedores", v: stats.providers, t: "stat-providers" },
        ].map((s, i) => (
          <div key={s.l} className={`p-6 ${i>0 ? "border-l border-clay-300" : ""}`} data-testid={s.t}>
            <div className="smallcaps">{s.l}</div>
            <div className="font-serif text-4xl tabular mt-2">{s.v}</div>
          </div>
        ))}
      </div>

      {/* Filters — same set for every user. Agent defaults to the caller
          so each user lands on their own trips; changing the dropdown
          exposes any teammate's or the whole team's view. */}
      <div className="mb-4 grid grid-cols-[260px_170px_1fr_auto] gap-3 items-center" data-testid="dashboard-filters">
        <select data-testid="filter-agent" value={filterAgent} onChange={(e) => setFilterAgent(e.target.value)} className="bg-white border border-clay-300 px-3 py-2 text-sm">
          <option value="">Agente: todos</option>
          {agents.map((a) => (
            <option key={a} value={a}>
              {a === user?.email ? "Mis itinerarios" : agentName(a)} ({a})
            </option>
          ))}
        </select>
        <select data-testid="filter-status" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="bg-white border border-clay-300 px-3 py-2 text-sm">
          <option value="">Estado: todos</option>
          {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v.text}</option>)}
        </select>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-3 text-clay-500" />
          <input data-testid="filter-traveler" placeholder="Buscar por nombre de cliente…" value={filterTraveler} onChange={(e) => setFilterTraveler(e.target.value)} className="w-full pl-9 pr-3 py-2 bg-white border border-clay-300 text-sm outline-none focus:border-terracotta" />
        </div>
        {hasFilters && (
          <button onClick={clearFilters} data-testid="clear-filters-btn" className="inline-flex items-center gap-1 px-3 py-2 border border-clay-300 hover:bg-clay-100 text-xs smallcaps">
            <X size={12}/> Sólo los míos
          </button>
        )}
      </div>

      <div className="smallcaps mb-3 flex items-center justify-between">
        <span>{isMyView ? "Mis viajes" : filterAgent ? "Viajes del agente" : "Todos los itinerarios"}</span>
        <div className="inline-flex border border-clay-300 divide-x divide-clay-300 normal-case tracking-normal"
             data-testid="view-mode-tabs">
          <button
            onClick={() => setViewMode("active")}
            className={`px-3 py-1.5 text-[11px] tracking-[0.18em] uppercase ${viewMode === "active" ? "bg-clay-900 text-white" : "bg-white text-clay-700 hover:bg-clay-100"}`}
            data-testid="tab-active">
            Activos
          </button>
          <button
            onClick={() => setViewMode("archived")}
            className={`px-3 py-1.5 text-[11px] tracking-[0.18em] uppercase inline-flex items-center gap-1.5 ${viewMode === "archived" ? "bg-clay-900 text-white" : "bg-white text-clay-700 hover:bg-clay-100"}`}
            data-testid="tab-archived">
            <Archive size={11}/> Archivados{archivedCount ? ` (${archivedCount})` : ""}
          </button>
        </div>
      </div>
      {viewMode === "archived" && (
        <div className="mb-3 border-l-4 border-terracotta bg-clay-50 px-4 py-3 text-xs text-clay-700 flex items-start gap-2"
             data-testid="archived-banner">
          <Archive size={13} className="mt-0.5 shrink-0 text-terracotta"/>
          <div>
            <strong className="text-clay-900">Vista de archivados.</strong> Estos itinerarios están ocultos del listado principal pero se conservan íntegros.
            Puedes restaurarlos cuando quieras{isAdmin ? " o borrarlos definitivamente (solo cuando no tienen pagos capturados)." : "."}
          </div>
        </div>
      )}
      <div className="border border-clay-300 bg-white">
        <div className={`grid ${gridCols} gap-0 bg-clay-100 text-[11px] tracking-[0.2em] uppercase text-clay-700 font-semibold`}>
          <div className="px-4 py-3">Nombre</div>
          <div className="px-4 py-3">Agente</div>
          <div className="px-4 py-3">Cliente</div>
          <div className="px-4 py-3">Fechas</div>
          <div className="px-4 py-3">Pax</div>
          <div className="px-4 py-3 text-right">Precio final</div>
          <div className="px-4 py-3 text-right">Acciones</div>
        </div>
        {loading ? (
          <div className="p-6 text-sm text-clay-700">Cargando…</div>
        ) : groups.length === 0 ? (
          <div className="p-10 text-center" data-testid="empty-state">
            <div className="font-serif text-2xl mb-2">
              {viewMode === "archived"
                ? "No hay itinerarios archivados"
                : hasFilters ? "Sin resultados" : "Aún no hay itinerarios"}
            </div>
            <p className="text-sm text-clay-700 mb-5">
              {viewMode === "archived"
                ? "Cuando archives un itinerario aparecerá aquí — la papelera nunca se vacía sola."
                : hasFilters ? "Ajusta o limpia los filtros." : "Crea tu primer viaje y empieza a sumar experiencias."}
            </p>
            {!hasFilters && viewMode === "active" && (
              <button onClick={openCreate} className="px-4 py-2 bg-terracotta text-white text-sm tracking-wider uppercase hover:bg-terracotta-hover">Crear itinerario</button>
            )}
          </div>
        ) : groups.map(({ gid, versions }) => {
          const latest = versions[0];
          const olderVersions = versions.slice(1);
          const isExpanded = expanded.has(gid);
          const renderRow = (itn, opts = {}) => {
            const t = calcTotals(itn);
            const st = STATUS_LABEL[itn.status] || STATUS_LABEL.draft;
            return (
              <div
                key={itn.itinerary_id}
                className={`grid ${gridCols} gap-0 border-t border-clay-300 hover:bg-clay-50 transition-colors text-sm ${opts.isOlder ? "bg-clay-50/50" : ""}`}
                data-testid={`itn-row-${itn.itinerary_id}`}
              >
                <div className={`px-4 py-3 flex items-center gap-3 min-w-0 ${opts.isOlder ? "pl-12" : ""}`}>
                  {!opts.isOlder && (versions.length > 1 ? (
                    <button
                      onClick={() => toggleGroup(gid)}
                      className="p-0.5 hover:bg-clay-200 -ml-1 shrink-0"
                      title={isExpanded ? "Ocultar versiones anteriores" : `Ver ${olderVersions.length} versión(es) anterior(es)`}
                      data-testid={`expand-group-${gid}`}
                    >
                      {isExpanded ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}
                    </button>
                  ) : <span className="w-[14px] shrink-0" />)}
                  <span className={`inline-block px-2 py-0.5 text-[10px] tracking-widest uppercase ${st.color} shrink-0`}>{st.text}</span>
                  {(itn.version || 1) > 1 && (
                    <span className="inline-block px-1.5 py-0.5 text-[10px] tracking-widest uppercase bg-terracotta/15 text-terracotta border border-terracotta/40 shrink-0">
                      v{itn.version}
                    </span>
                  )}
                  <button className="text-left font-semibold hover:text-terracotta truncate" onClick={() => navigate(`/itineraries/${itn.itinerary_id}`)}>
                    {itn.name}
                  </button>
                </div>
                <div className="px-4 py-3 text-clay-700 truncate" title={itn.created_by}>{agentName(itn.created_by)}</div>
                <div className="px-4 py-3 text-clay-700 truncate">{itn.main_traveler || "—"}</div>
                <div className="px-4 py-3 text-clay-700 tabular flex items-center gap-2"><Calendar size={13}/>{fmt(itn.start_date)} → {fmt(itn.end_date)}</div>
                <div className="px-4 py-3 text-clay-700 tabular flex items-center gap-1"><UsersIcon size={13}/>{itn.num_travelers}</div>
                <div className="px-4 py-3 text-right font-semibold tabular">€ {t.final.toLocaleString("es-ES", { maximumFractionDigits: 2 })}</div>
                <div className="px-4 py-3 flex items-center justify-end gap-1">
                  <button onClick={() => navigate(`/itineraries/${itn.itinerary_id}`)} className="p-1.5 hover:bg-clay-200" title="Editar" data-testid={`edit-${itn.itinerary_id}`}>
                    <Pencil size={14} />
                  </button>
                  <button onClick={() => duplicate(itn.itinerary_id)} className="p-1.5 hover:bg-clay-200" title="Duplicar como nueva versión" data-testid={`dup-${itn.itinerary_id}`}>
                    <Copy size={14} />
                  </button>
                  {/* Excel export hidden — the team works directly off Sofi.
                  <button onClick={() => exportXlsx(itn.itinerary_id, itn.name)} className="p-1.5 hover:bg-clay-200" title="Exportar Excel" data-testid={`export-${itn.itinerary_id}`}>
                    <FileDown size={14} />
                  </button>
                  */}
                  {viewMode === "active" && (
                    <button
                      onClick={(e) => { e.stopPropagation(); archive(itn.itinerary_id); }}
                      className="p-1.5 hover:bg-clay-200 text-clay-700 hover:text-terracotta"
                      title="Archivar (soft-delete, se puede restaurar)"
                      data-testid={`archive-${itn.itinerary_id}`}
                    >
                      <Archive size={14} />
                    </button>
                  )}
                  {viewMode === "archived" && (
                    <>
                      <button
                        onClick={(e) => { e.stopPropagation(); restore(itn.itinerary_id); }}
                        className="p-1.5 hover:bg-clay-200 text-clay-700 hover:text-pine"
                        title="Restaurar itinerario"
                        data-testid={`restore-${itn.itinerary_id}`}
                      >
                        <RotateCcw size={14} />
                      </button>
                      {isAdmin && (
                        <button
                          onClick={(e) => { e.stopPropagation(); purge(itn.itinerary_id, itn.name); }}
                          className="p-1.5 hover:bg-clay-200 text-destructive"
                          title="Borrar permanentemente (solo admin, bloqueado si hay pagos capturados)"
                          data-testid={`purge-${itn.itinerary_id}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          };
          return (
            <React.Fragment key={gid}>
              {renderRow(latest)}
              {isExpanded && olderVersions.map((v) => renderRow(v, { isOlder: true }))}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}
